import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { CheckCircle2, Loader2, XCircle, Clock, FileText, ShieldCheck } from "lucide-react";

const MAX_ATTEMPTS = 8;
const INTERVAL = 2000;

export default function BookingSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/checkout/status/${sessionId}`);
        if (cancelled) return;
        setBooking(data.booking);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("error"); return; }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) { setState("pending"); return; }
        setTimeout(poll, INTERVAL);
      } catch {
        if (cancelled) return;
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) { setState("error"); return; }
        setTimeout(poll, INTERVAL);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const fr = t("bookingForm");

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-24" data-testid="booking-success-page">
      <div className="max-w-md w-full rounded-3xl bg-white border border-black/5 shadow-[0_16px_48px_rgba(44,48,48,0.1)] p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-brand mb-4 animate-spin" strokeWidth={1.5} />
            <p className="text-ink-600">Vérification de votre paiement…</p>
          </>
        )}

        {state === "paid" && (
          <div data-testid="booking-success-paid">
            <CheckCircle2 size={56} className="mx-auto text-brand mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-3xl text-ink-900 mb-2">Réservation confirmée</h1>
            <p className="text-ink-600 mb-5">Merci ! Votre acompte a bien été reçu et votre séjour est confirmé.</p>
            {booking && (
              <div className="rounded-xl bg-cream border border-black/5 p-4 text-left text-sm space-y-1.5 mb-6">
                <div className="flex justify-between"><span className="text-ink-400">Logement</span><span className="text-ink-900 font-medium">{booking.accommodation_name}</span></div>
                <div className="flex justify-between"><span className="text-ink-400">Séjour</span><span className="text-ink-900">{booking.arrivee} → {booking.depart}</span></div>
                {booking.total != null && <div className="flex justify-between"><span className="text-ink-400">Total du séjour</span><span className="text-ink-900">{booking.total} €</span></div>}
                {booking.paid_amount != null && <div className="flex justify-between"><span className="text-ink-400">Acompte réglé</span><span className="text-ink-900 font-medium">{booking.paid_amount} €</span></div>}
                {booking.balance != null && <div className="flex justify-between"><span className="text-ink-400">Solde sur place</span><span className="text-ink-900">{booking.balance} €</span></div>}
                {booking.tourist_tax != null && booking.tourist_tax > 0 && <div className="flex justify-between"><span className="text-ink-400">Taxe de séjour (sur place)</span><span className="text-ink-900">{booking.tourist_tax} €</span></div>}
                {booking.caution ? <div className="flex justify-between"><span className="text-ink-400">Caution (empreinte sur place)</span><span className="text-ink-900">{booking.caution} €</span></div> : null}
              </div>
            )}
            {booking && (booking.balance != null || booking.caution) && (
              <p className="text-xs text-ink-400 mb-3">Le solde, la taxe de séjour et la caution se règlent uniquement sur place, en espèces ou par carte bancaire.</p>
            )}
            {booking?.id && (
              <a href={`${API}/bookings/${booking.id}/contrat.pdf`} target="_blank" rel="noreferrer" className="mb-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-brand text-brand px-6 py-3 font-medium hover:bg-brand-50 transition-colors" data-testid="download-contract-btn">
                <FileText size={18} /> Télécharger le contrat de location (PDF)
              </a>
            )}
            <Link to={booking?.id ? `/fiche-police/${booking.id}` : "/fiche-police"} className="mb-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-50 text-brand px-6 py-3 font-medium hover:bg-brand-100 transition-colors" data-testid="police-link-btn">
              <ShieldCheck size={18} /> Fiche de police (voyageurs étrangers)
            </Link>
            <p className="text-xs text-ink-400 mb-4">Un e-mail de confirmation avec votre contrat de location vous sera envoyé.</p>
            <Link to="/" className="inline-block rounded-full bg-brand text-white px-6 py-3 font-medium hover:bg-brand-600 transition-colors" data-testid="success-home-btn">Retour à l'accueil</Link>
          </div>
        )}

        {state === "pending" && (
          <>
            <Clock size={48} className="mx-auto text-amber-500 mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl text-ink-900 mb-2">Paiement en cours de traitement</h1>
            <p className="text-ink-600 mb-6">Votre paiement est en cours de vérification. Vous recevrez une confirmation par e-mail. {fr.cancellation}</p>
            <Link to="/" className="inline-block rounded-full border border-ink-400/30 text-ink-700 px-6 py-3 font-medium">Retour à l'accueil</Link>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-red-500 mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl text-ink-900 mb-2">Paiement non finalisé</h1>
            <p className="text-ink-600 mb-6">Le paiement n'a pas pu être confirmé. Aucun montant n'a été débité si la transaction a échoué. Vous pouvez réessayer.</p>
            <Link to="/" className="inline-block rounded-full bg-brand text-white px-6 py-3 font-medium hover:bg-brand-600 transition-colors">Retour à l'accueil</Link>
          </>
        )}
      </div>
    </div>
  );
}
